// useBoardData.ts — Sprint 15. Owns the BoardManagement page's
// store-bound surface. Eleven mutations spread across three
// resource families (members / meetings / resolutions) — bundling
// them here keeps the orchestrator free of useCondoStore() entirely.
import { useEffect } from 'react';
import { staticApi } from '../../utils/staticApi';
import dayjs from 'dayjs';
import { useCondoStore } from '../../store/condoStore';
import type {
  BoardMember, BoardMeeting, BoardResolution, MinuteItem,
} from '../../types';
import { MEETING_TYPE_CONFIG, type MeetingType } from './boardTypes';

export interface UseBoardDataArgs {
  condoId?: string;
}

export interface UseBoardData {
  owners:           any[];
  boardMembers:     BoardMember[];
  boardMeetings:    BoardMeeting[];
  boardResolutions: BoardResolution[];

  // ── Members ──────────────────────────────────────────────────
  saveMember:   (values: any, editingId?: string) => Promise<void>;
  deleteMember: (id: string) => Promise<void>;

  // ── Meetings ─────────────────────────────────────────────────
  saveMeeting:    (values: any, editing: BoardMeeting | null) => Promise<void>;
  deleteMeeting:  (id: string) => Promise<void>;
  /** Append a single MinuteItem to the meeting's `minutes` list. */
  appendMinutes:  (meeting: BoardMeeting, item: MinuteItem) => Promise<BoardMeeting>;

  // ── Resolutions ──────────────────────────────────────────────
  saveResolution:   (values: any, editingId?: string) => Promise<void>;
  closeResolution:  (resolution: BoardResolution) => Promise<void>;
  deleteResolution: (id: string) => Promise<void>;
  castVote:         (resolution: BoardResolution, vote: 'FOR' | 'AGAINST' | 'ABSTAIN') => Promise<void>;
}

export function useBoardData({ condoId }: UseBoardDataArgs): UseBoardData {
  const {
    owners,
    boardMembers, boardMeetings, boardResolutions,
    fetchBoardMembers, createBoardMember, updateBoardMember, deleteBoardMember,
    fetchBoardMeetings, createBoardMeeting, updateBoardMeeting, deleteBoardMeeting,
    fetchBoardResolutions, createBoardResolution, updateBoardResolution,
    closeBoardResolution, deleteBoardResolution, castResolutionVote,
  } = useCondoStore() as any;

  useEffect(() => {
    fetchBoardMembers(condoId);
    fetchBoardMeetings(condoId);
    fetchBoardResolutions(condoId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condoId]);

  // ── Member mutations ──────────────────────────────────────────────
  const saveMember = async (values: any, editingId?: string) => {
    const payload = { ...values, condoId: condoId || '' };
    try {
      if (editingId) {
        await updateBoardMember(editingId, payload);
        staticApi.message.success('Board member updated');
      } else {
        await createBoardMember(payload);
        staticApi.message.success(`${values.name} added to the board`);
      }
    } catch {
      staticApi.message.error('Failed to save board member');
      throw new Error('saveMember failed');
    }
  };

  const deleteMember = async (id: string) => {
    try {
      await deleteBoardMember(id);
      staticApi.message.success('Board member removed');
    } catch {
      staticApi.message.error('Failed to remove board member');
    }
  };

  // ── Meeting mutations ─────────────────────────────────────────────
  const saveMeeting = async (values: any, editing: BoardMeeting | null) => {
    const payload = {
      ...values,
      condoId:   condoId || '',
      attendees: editing?.attendees ?? [],
      agenda:    editing?.agenda    ?? [],
      minutes:   editing?.minutes   ?? [],
      createdAt: editing?.createdAt ?? dayjs().toISOString(),
    };
    try {
      if (editing) {
        await updateBoardMeeting(editing.id, payload);
        staticApi.message.success('Meeting updated');
      } else {
        await createBoardMeeting(payload);
        const label = MEETING_TYPE_CONFIG[values.type as MeetingType]?.label ?? 'Meeting';
        staticApi.message.success(`${label} scheduled`);
      }
    } catch {
      staticApi.message.error('Failed to save meeting');
      throw new Error('saveMeeting failed');
    }
  };

  const deleteMeeting = async (id: string) => {
    try {
      await deleteBoardMeeting(id);
      staticApi.message.success('Meeting deleted');
    } catch {
      staticApi.message.error('Failed to delete meeting');
    }
  };

  const appendMinutes = async (
    meeting: BoardMeeting, item: MinuteItem,
  ): Promise<BoardMeeting> => {
    const updatedMinutes = [...meeting.minutes, item];
    try {
      await updateBoardMeeting(meeting.id, { ...meeting, minutes: updatedMinutes });
      staticApi.message.success('Minutes entry added');
      return { ...meeting, minutes: updatedMinutes };
    } catch {
      staticApi.message.error('Failed to save minutes');
      throw new Error('appendMinutes failed');
    }
  };

  // ── Resolution mutations ──────────────────────────────────────────
  const saveResolution = async (values: any, editingId?: string) => {
    const payload = { ...values, condoId: condoId || '' };
    try {
      if (editingId) {
        await updateBoardResolution(editingId, payload);
        staticApi.message.success('Resolution updated');
      } else {
        await createBoardResolution(payload);
        staticApi.message.success(`Resolution ${values.voteNumber} created`);
      }
    } catch {
      staticApi.message.error('Failed to save resolution');
      throw new Error('saveResolution failed');
    }
  };

  const closeResolution = async (v: BoardResolution) => {
    try {
      await closeBoardResolution(v.id);
      staticApi.message.success(`Resolution ${v.voteNumber} closed`);
    } catch {
      staticApi.message.error('Failed to close resolution');
    }
  };

  const deleteResolution = async (id: string) => {
    try {
      await deleteBoardResolution(id);
      staticApi.message.success('Resolution deleted');
    } catch {
      staticApi.message.error('Failed to delete resolution');
    }
  };

  const castVote = async (
    v: BoardResolution, vote: 'FOR' | 'AGAINST' | 'ABSTAIN',
  ) => {
    try {
      await castResolutionVote(v.id, vote);
      staticApi.message.success(`Vote ${vote} cast on ${v.voteNumber}`);
    } catch (err: any) {
      staticApi.message.error(err?.message || 'Failed to cast vote');
    }
  };

  return {
    owners, boardMembers, boardMeetings, boardResolutions,
    saveMember, deleteMember,
    saveMeeting, deleteMeeting, appendMinutes,
    saveResolution, closeResolution, deleteResolution, castVote,
  };
}
